import { X, Type, Image, Images, Video, MessageCircle, HelpCircle, Star, ArrowRight, Code, Users, Clock, Lightbulb, ShoppingBag, PlayCircle } from 'lucide-react';
import type { BlockType } from '../../types/site';

interface AddBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: BlockType) => void;
}

interface BlockCategory {
  name: string;
  blocks: {
    type: BlockType;
    label: string;
    description: string;
    icon: typeof Type;
  }[];
}

const BLOCK_CATEGORIES: BlockCategory[] = [
  {
    name: 'Inhalte',
    blocks: [
      { type: 'TEXT', label: 'Text', description: 'Titel und Textinhalt', icon: Type },
      { type: 'IMAGE', label: 'Bild', description: 'Einzelnes Bild mit Beschriftung', icon: Image },
      { type: 'IMAGE_TEXT', label: 'Bild & Text', description: 'Bild neben Text', icon: Images },
      { type: 'IMAGE_GALLERY', label: 'Bildergalerie', description: '2-4 Bilder nebeneinander', icon: Images },
      { type: 'BEFORE_AFTER', label: 'Vorher/Nachher', description: 'Zwei Bilder im Vergleich', icon: Images },
    ],
  },
  {
    name: 'Video',
    blocks: [
      { type: 'VIDEO', label: 'Video', description: 'YouTube Video einbetten', icon: Video },
      { type: 'ADVANCED_VIDEO', label: 'Erweitertes Video', description: 'YouTube oder eigenes Video', icon: PlayCircle },
    ],
  },
  {
    name: 'Features & Vorteile',
    blocks: [
      { type: 'BENEFITS', label: 'Vorteile', description: 'Icon-Karten mit Vorteilen', icon: Star },
      { type: 'HIGHLIGHTS', label: 'Highlights', description: '3-4 Highlight-Karten', icon: Star },
      { type: 'STEPS', label: 'Schritte', description: '3-5 Schritte erklärt', icon: ArrowRight },
      { type: 'TIMELINE', label: 'Timeline', description: 'Zeitstrahl oder Meilensteine', icon: Clock },
      { type: 'PROBLEM_SOLUTION', label: 'Problem & Lösung', description: 'Probleme und Lösungen', icon: Lightbulb },
    ],
  },
  {
    name: 'Personen & Produkte',
    blocks: [
      { type: 'TEAM', label: 'Team', description: 'Teammitglieder vorstellen', icon: Users },
      { type: 'PRODUCT_SHOWCASE', label: 'Produkt-Showcase', description: 'Produkte präsentieren', icon: ShoppingBag },
      { type: 'TESTIMONIALS', label: 'Erfahrungen', description: 'Kundenstimmen', icon: MessageCircle },
      { type: 'SOCIAL_PROOF', label: 'Social Proof', description: 'Zahlen und Fakten', icon: Users },
    ],
  },
  {
    name: 'Interaktion',
    blocks: [
      { type: 'FAQ', label: 'FAQ', description: 'Fragen und Antworten', icon: HelpCircle },
      { type: 'CTA', label: 'Call-to-Action', description: 'Handlungsaufforderung', icon: ArrowRight },
      { type: 'WHATSAPP_BUTTON', label: 'WhatsApp Button', description: 'Kontakt per WhatsApp', icon: MessageCircle },
      { type: 'CUSTOM_HTML', label: 'Custom HTML', description: 'Eigener HTML-Code', icon: Code },
    ],
  },
];

export function AddBlockModal({ isOpen, onClose, onAdd }: AddBlockModalProps) {
  if (!isOpen) return null;

  const handleAdd = (type: BlockType) => {
    onAdd(type);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 !mt-0">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Block hinzufügen</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-8">
          {BLOCK_CATEGORIES.map((category) => (
            <div key={category.name}>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                {category.name}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {category.blocks.map(({ type, label, description, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => handleAdd(type)}
                    className="group flex flex-col items-start p-4 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-teal-500 dark:hover:border-teal-400 hover:bg-teal-50/30 dark:hover:bg-teal-900/20 transition-all duration-200 text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-slate-700 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/50 flex items-center justify-center mb-3 transition-colors">
                      <Icon className="w-5 h-5 text-gray-600 dark:text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{label}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{description}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
